import { Plus, RotateCw, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, Checkbox, Input } from "~/components/ui";
import { useGroupActivation } from "~/features/admin/groups/hooks";
import type { GroupRow } from "~/features/admin/groups/types";
import { GroupActivationDialog } from "./GroupActivationDialog";
import { GroupFormDialog } from "./GroupFormDialog";
import { GroupPeek } from "./GroupPeek";
import { GroupsTable } from "./GroupsTable";

interface GroupsPresenterProps {
  rows: GroupRow[];
  pageIndex: number;
  onPageIndexChange: (next: number) => void;
  pageSize: number;
  onPageSizeChange: (next: number) => void;
  onPageReset: () => void;
  onRefresh: () => void;
  selectedGroup: string | null;
  onSelect: (id: string) => void;
  onCloseSelected: () => void;
}

interface PendingActivation {
  group: GroupRow;
  action: "activate" | "deactivate";
}

export function GroupsPresenter({
  rows,
  pageIndex,
  onPageIndexChange,
  pageSize,
  onPageSizeChange,
  onPageReset,
  onRefresh,
  selectedGroup,
  onSelect,
  onCloseSelected,
}: GroupsPresenterProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [excludeDeactivated, setExcludeDeactivated] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<GroupRow | null>(null);
  const [pending, setPending] = useState<PendingActivation | null>(null);

  const { onActivate, onDeactivate, processing } = useGroupActivation();

  const filteredRows = useMemo(
    () => (excludeDeactivated ? rows.filter((row) => !row.isDeactivated) : rows),
    [rows, excludeDeactivated],
  );

  const onSearchChange = (next: string) => {
    setSearch(next);
    onPageReset();
  };

  const onExcludeDeactivatedChange = (next: boolean) => {
    setExcludeDeactivated(next);
    onPageReset();
  };

  const handleConfirm = async () => {
    if (!pending) return;
    if (pending.action === "activate") {
      await onActivate({ id: pending.group.id });
    } else {
      await onDeactivate({ id: pending.group.id });
    }
    setPending(null);
  };

  return (
    <div className="mx-auto max-w-[var(--content-max)] space-y-5 p-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h1 className="text-dewey-title capitalize">{t("model.group.modelNames")}</h1>
        <div className="flex flex-wrap items-center gap-2.5">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-3" />
            <Input
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={t("ui.search")}
              className="h-9 w-[240px] pl-8"
              aria-label={t("ui.search")}
            />
          </div>
          <Button type="button" variant="outline" size="icon" onClick={onRefresh} aria-label={t("ui.reload")}>
            <RotateCw />
          </Button>
          <label
            htmlFor="groups-exclude-deactivated"
            className="inline-flex items-center gap-2 text-14-regular text-fg-2"
          >
            <Checkbox
              id="groups-exclude-deactivated"
              checked={excludeDeactivated}
              onCheckedChange={(v) => onExcludeDeactivatedChange(v === true)}
            />
            <span>{t("ui.excludeDeactivated")}</span>
          </label>
          <Button type="button" size="sm" onClick={() => setCreateOpen(true)}>
            <Plus />
            <span>{t("ui.add")}</span>
          </Button>
        </div>
      </div>

      <GroupFormDialog open={createOpen} onOpenChange={setCreateOpen} />
      {editingGroup && (
        <GroupFormDialog
          open={editingGroup !== null}
          onOpenChange={(next) => !next && setEditingGroup(null)}
          group={{ id: editingGroup.id, name: editingGroup.name, description: editingGroup.description }}
        />
      )}

      <GroupsTable
        rows={filteredRows}
        onSelect={onSelect}
        onEdit={setEditingGroup}
        onActivate={(group) => setPending({ group, action: "activate" })}
        onDeactivate={(group) => setPending({ group, action: "deactivate" })}
        emptyMessage={t("ui.notFound")}
        globalFilter={search}
        onGlobalFilterChange={onSearchChange}
        pageIndex={pageIndex}
        onPageIndexChange={onPageIndexChange}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
      />

      {pending && (
        <GroupActivationDialog
          open={pending !== null}
          action={pending.action}
          name={pending.group.name}
          processing={processing}
          onConfirm={handleConfirm}
          onCancel={() => setPending(null)}
        />
      )}

      <GroupPeek id={selectedGroup} onClose={onCloseSelected} />
    </div>
  );
}
